// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import { isEmpty, pathOr } from 'ramda';

import Loader from '../../components/loader';
import BarChart from '../../components/charts/bar';
import colors from '../../styles/colors';
import infoIcon from '../../styles/icons/Dialog-Box-About-24.png';
import ExportButton from '../../components/export';
import VersionTextMessage from '../../utils/versionText';

import ActionTypes from './action-types';

import type { TopEarlyFinishedActivitiesState } from './reducer';
import type { EarlyFinishedActivity } from './sagas';
import type { Reducers } from '../../reducers';
import type { Project } from '../../auth/with-project-context';

type Action = {
  type: $Values<typeof ActionTypes> | 'showModal',
  data?: Object,
};
type Dispatch = (action: Action) => void;

type Props = {
  dispatch: Dispatch,
  data: TopEarlyFinishedActivitiesState,
  project: Project,
  week: string,
  versionDates: string;
};

type ChartData = {
  label: string,
  value: number,
  color: string,
  text: string,
};

const styles: Object = {
  // header: {
  //   fontSize: '1.2rem',
  //   textAlign: 'center',
  //   marginBottom: '2rem',
  // },
  widget: {
    border: '1px solid rgb(220, 220, 220)',
    padding: '1rem',
    background: '#fff',
    boxShadow: '0 0 6px 3px rgba(156, 156, 156, 0.1)',
  },
  table: {
    width: '100%',
  },
  taskNameTable: {
    maxWidth: '300px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  // parentHierarchy: {
  //   fontSize: '0.70rem',
  //   marginTop: '0.2rem',
  //   textOverflow: 'ellipsis',
  //   overflow: 'hidden',
  //   whiteSpace: 'nowrap',
  // },
  noData: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20px',
  },
};

/* eslint-disable */
const getHeight: Object = {
  '1': '140',
  '2': '175',
  '3': '207',
  '4': '240',
  '5': '270',
  '6': '302',
  '7': '334',
  '8': '366',
  '9': '398',
  '10': '430',
};
/* eslint-enable */

class TopEarlyFinishedActivities extends React.PureComponent<Props> {
  componentDidMount() {
    if (this.props.week !== '') {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.week !== '' && prevProps.week !== this.props.week) {
      this.fetchData();
    }
  }

  // getParentHierarchy(activity: EarlyFinishedActivity): string {
  //   const parents: Array<string> = activity.parent.split(' | ').reverse();
  //   parents.pop();
  //   return parents.join(' ➔ ');
  // }

  getData(): ChartData[] {
    return this.props.data.chartData.map(
      (activity: EarlyFinishedActivity): ChartData => ({
        label: activity.name + activity.wbs,
        value: parseInt(activity.end_date_difference, 10) * (window.__r2d2 ? 1 : -1),
        color: colors.themeSecondaryColor,
        text:
          `${activity.wbs} ${activity.name.trim()}<br>Actual end date: ${moment(
            activity.actual_end_date,
          ).format('DD MMM YY')}<br>`
          + `Base end date: ${moment(activity.base_end_date).format(
            'DD MMM YY',
          )}<br>Early by: ${activity.end_date_difference * (window.__r2d2 ? 1 : -1)} day(s)`,
      }),
    );
  }

  // getXAxisLabels(): string[] {
  //   return this.props.data.chartData.map((activity: EarlyFinishedActivity): string => {
  //     if (activity.name.length > 20) return `${activity.name.substring(0, 15)}...`;

  //     return activity.name;
  //   });
  // }

  showActivityDetailModal = (activity: EarlyFinishedActivity): null => {
    // if (window.__r2d2) {
    //   return null;
    // }
    this.props.dispatch({
      type: 'showModal',
      modalType: 'activityDetail',
      modalProps: {
        activityId: activity.id,
        activityName: activity.name,
      },
    });
    return null;
  };

  fetchData() {
    this.props.dispatch({
      type: ActionTypes.GET_TOP_EARLY_FINISHED_ACTIVITIES,
      data: {
        projectId: this.props.project.id,
        startDate: this.props.week,
      },
    });
  }

  renderTable(): React.Element<any> {
    return (
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.taskNameTable}>Activity</th>
            <th className="hide-element">Base End date</th>
            <th className="hide-element">Actual End date</th>
            <th>Early by (days)</th>
          </tr>
        </thead>
        <tbody>
          {this.props.data.chartData.map(
            (activity: EarlyFinishedActivity, i: number): React.Node => (
              <tr key={i}>
                <td style={styles.taskNameTable}>
                  <a
                    onClick={this.showActivityDetailModal.bind(this, activity)}
                    className="underline-hover"
                  >
                    {activity.name}
                  </a>
                </td>
                <td className="hide-element">
                  {moment(activity.base_end_date).format('DD MMM YY')}
                </td>
                <td className="hide-element">
                  {moment(activity.actual_end_date).format('DD MMM YY')}
                </td>
                <td>{activity.end_date_difference * (window.__r2d2 ? 1 : -1)}</td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    );
  }

  renderWidget(options: Object): React.Node {
    return this.props.data.chartData.length !== 0
      ? (
        <div>
          { this.props.week ? <ExportButton projectId={this.props.project.id} startDate={this.props.week} widgetName="top_early_finished_activities" /> : null }
          <div style={{ display: 'flex', width: '100%' }}>
            <div style={{ width: '60%' }}>
              {this.renderTable()}
            </div>
            <div style={{ width: '40%' }}>
              <BarChart data={this.getData()} options={options} dataOptions={{ orientation: 'h' }} />
            </div>
          </div>
        </div>)
      : <span style={styles.noData}>No data available</span>;
  }

  render(): React.Node {
    const options: Object = {
      height: getHeight[this.props.data.chartData.length],
      // height: '200',
      yaxis: {
        showticklabels: false,
      },
      xaxis: {
        title: 'Days Shortened (end date)',
      },
      margin: {
        t: 35,
        r: 10,
        l: 3,
      },
    };
    /* eslint max-len: ["error", { "ignoreStrings": true, "code": 150}] */
    return (
      <section style={styles.widget}>
        <h1 className="header-widget-common">
          Top Early Finished Activities
          <img
            data-tip="This chart lists the top early finished activities sorted based on early finish"
            width="14"
            src={infoIcon}
            alt="i"
          />
        </h1>
        <ReactTooltip place="top" type="dark" effect="solid" border multiline />
        {
          this.props.data.loading === null ? <Loader /> : this.renderWidget(options)
        }
        {
          this.props.data.loading === false && !isEmpty(this.props.versionDates)
          && <VersionTextMessage versionDates={this.props.versionDates} />
        }
      </section>
    );
  }
}

const component: any = connect(
  (state: Reducers): { data: TopEarlyFinishedActivitiesState } => ({
    data: state.topEarlyFinishedActivities,
    versionDates: pathOr({}, ['project', 'versionDates'], state),
  }),
)(TopEarlyFinishedActivities);

component.style = {
  width: '98%',
};

export default component;
